import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Icon,
  Chip,
  alpha,
  useTheme,
  Tooltip,
} from "@mui/material";
import { IChartOfAccount } from "../../../accounting.interface";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";

interface AccountTableViewProps {
  accounts: IChartOfAccount[];
  selectedAccount?: IChartOfAccount;
  onNodeClick: (account: IChartOfAccount) => void;
  onAddSubAccount: (parentAccount: IChartOfAccount) => void;
}

interface TableRowData extends IChartOfAccount {
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
}

export const AccountTableView: React.FC<AccountTableViewProps> = ({
  accounts,
  selectedAccount,
  onNodeClick,
  onAddSubAccount,
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  // Initialize with all rows expanded by default
  const getAllAccountIds = useCallback(
    (accounts: IChartOfAccount[]): string[] => {
      const ids: string[] = [];
      const traverse = (accs: IChartOfAccount[]) => {
        accs.forEach((acc) => {
          ids.push(acc.id);
          if (acc.children) {
            traverse(acc.children);
          }
        });
      };
      traverse(accounts);
      return ids;
    },
    []
  );

  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => {
    return new Set(getAllAccountIds(accounts));
  });

  // Update expanded rows when accounts change
  useEffect(() => {
    setExpandedRows(new Set(getAllAccountIds(accounts)));
  }, [accounts, getAllAccountIds]);

  // Flatten accounts into table rows with depth information
  const tableRows = useMemo(() => {
    const rows: TableRowData[] = [];

    const processAccounts = (
      accounts: IChartOfAccount[],
      depth: number = 0
    ) => {
      accounts.forEach((account) => {
        const hasChildren = !!(account.children && account.children.length > 0);
        const isExpanded = expandedRows.has(account.id);

        rows.push({
          ...account,
          depth,
          hasChildren,
          isExpanded,
        });

        // Add children if expanded
        if (hasChildren && isExpanded && account.children) {
          processAccounts(account.children, depth + 1);
        }
      });
    };

    processAccounts(accounts);
    return rows;
  }, [accounts, expandedRows]);

  const handleToggleExpand = (accountId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedRows(newExpanded);
  };

  const formatBalance = (balance?: number, currency: string = "BHD") => {
    if (balance === undefined || balance === null) {
      return "—";
    }
    return `${currency} ${balance.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getAccountTypeColor = (accountType: string) => {
    switch (accountType) {
      case "Asset":
        return "#10B981";
      case "Liability":
        return "#EF4444";
      case "Equity":
        return "#3B82F6";
      case "Revenue":
        return "#8B5CF6";
      case "Expense":
        return "#F59E0B";
      default:
        return theme.palette.text.secondary;
    }
  };

  const getAccountTypeIcon = (accountType: string) => {
    switch (accountType) {
      case "Asset":
        return "account_balance_wallet";
      case "Liability":
        return "credit_card";
      case "Equity":
        return "trending_up";
      case "Revenue":
        return "monetization_on";
      case "Expense":
        return "money_off";
      default:
        return "account_balance";
    }
  };

  return (
    <TableContainer
      sx={{
        maxHeight: "calc(100vh - 400px)",
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderRadius: 1,
        "& .MuiTableCell-root": {
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        },
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "text.primary",
                py: 2,
              }}
            >
              Account Name
            </TableCell>
            <TableCell
              align="center"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "text.primary",
                py: 2,
                minWidth: 100,
              }}
            >
              Code
            </TableCell>
            <TableCell
              align="center"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "text.primary",
                py: 2,
                minWidth: 80,
              }}
            >
              Type
            </TableCell>
            <TableCell
              align="right"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "text.primary",
                py: 2,
                minWidth: 150,
              }}
            >
              Opening Balance
            </TableCell>
            <TableCell
              align="right"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "text.primary",
                py: 2,
                minWidth: 150,
              }}
            >
              Current Balance
            </TableCell>
            <TableCell
              align="center"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "text.primary",
                py: 2,
                minWidth: 100,
              }}
            >
              Status
            </TableCell>
            <TableCell
              align="center"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "text.primary",
                py: 2,
                minWidth: 80,
              }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map((row) => (
            <TableRow
              key={row.id}
              hover
              selected={selectedAccount?.id === row.id}
              onClick={() => onNodeClick(row)}
              sx={{
                cursor: "pointer",
                "&.Mui-selected": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                },
                "&:hover": {
                  bgcolor: alpha(theme.palette.action.hover, 0.05),
                },
                ...(row.isActive === false && {
                  opacity: 0.6,
                  "& .MuiTableCell-root": {
                    color: "text.disabled",
                  },
                }),
              }}
            >
              <TableCell>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pl: row.depth * 3, // Indent based on depth
                  }}
                >
                  {/* Expand/Collapse Button */}
                  {row.hasChildren ? (
                    <IconButton
                      size="small"
                      onClick={(e) => handleToggleExpand(row.id, e)}
                      sx={{
                        width: 20,
                        height: 20,
                        color: "text.secondary",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.action.hover, 0.1),
                        },
                      }}
                    >
                      <Icon sx={{ fontSize: 16 }}>
                        {row.isExpanded ? "expand_less" : "chevron_right"}
                      </Icon>
                    </IconButton>
                  ) : (
                    <Box sx={{ width: 20 }} /> // Spacer for alignment
                  )}

                  {/* Account Type Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      bgcolor: alpha(getAccountTypeColor(row.accountType), 0.1),
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 14,
                        color: getAccountTypeColor(row.accountType),
                      }}
                    >
                      {getAccountTypeIcon(row.accountType)}
                    </Icon>
                  </Box>

                  {/* Account Name */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: row.isParent ? 600 : 400,
                      color: row.isParent ? "text.primary" : "text.secondary",
                      fontSize: "0.875rem",
                    }}
                  >
                    {row.accountName}
                  </Typography>

                  {/* Parent Account Indicator */}
                  {row.isParent && (
                    <Chip
                      size="small"
                      label="Parent"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: "info.main",
                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                    />
                  )}
                </Box>
              </TableCell>

              <TableCell align="center">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "text.primary",
                  }}
                >
                  {row.accountCode}
                </Typography>
              </TableCell>

              <TableCell align="center">
                <Chip
                  size="small"
                  label={row.accountType}
                  sx={{
                    height: 24,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    bgcolor: alpha(getAccountTypeColor(row.accountType), 0.1),
                    color: getAccountTypeColor(row.accountType),
                    border: `1px solid ${alpha(
                      getAccountTypeColor(row.accountType),
                      0.2
                    )}`,
                    "& .MuiChip-label": {
                      px: 1.5,
                    },
                  }}
                />
              </TableCell>

              <TableCell align="right">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    fontWeight: row.openingBalance !== undefined ? 600 : 400,
                    color:
                      row.openingBalance !== undefined
                        ? "text.primary"
                        : "text.disabled",
                  }}
                >
                  {formatBalance(row.openingBalance, row.currency)}
                </Typography>
              </TableCell>

              <TableCell align="right">
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    fontWeight: row.currentBalance !== undefined ? 600 : 400,
                    color:
                      row.currentBalance !== undefined
                        ? "text.primary"
                        : "text.disabled",
                  }}
                >
                  {formatBalance(row.currentBalance, row.currency)}
                </Typography>
              </TableCell>

              <TableCell align="center">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Chip
                    size="small"
                    label={row.isActive === false ? "Inactive" : "Active"}
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      bgcolor:
                        row.isActive === false
                          ? alpha(theme.palette.error.main, 0.1)
                          : alpha(theme.palette.success.main, 0.1),
                      color:
                        row.isActive === false ? "error.main" : "success.main",
                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                  />
                  {!row.allowDirectPosting && (
                    <Tooltip title="No Direct Posting" arrow>
                      <Icon
                        sx={{
                          fontSize: 16,
                          color: "warning.main",
                        }}
                      >
                        block
                      </Icon>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>

              <TableCell align="center">
                {row.isActive !== false ? (
                  <Tooltip
                    title={
                      hasPermission(
                        "accounting.chart_of_accounts.create",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                        ? "Add Sub-Account"
                        : "You don't have permission to add sub-accounts"
                    }
                    arrow
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddSubAccount(row);
                        }}
                        disabled={
                          !hasPermission(
                            "accounting.chart_of_accounts.create",
                            user?.newPermissions,
                            user?.assignment_details?.id,
                            user?.is_store_owner
                          )
                        }
                        sx={{
                          width: 28,
                          height: 28,
                          color: "primary.main",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: 16 }}>add</Icon>
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title="Inactive accounts cannot have sub-accounts added"
                    arrow
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        bgcolor: alpha(theme.palette.action.disabled, 0.1),
                      }}
                    >
                      <Icon sx={{ fontSize: 16, color: "action.disabled" }}>
                        block
                      </Icon>
                    </Box>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
